import React, { useState } from 'react';
import {
  notify,
  KoobButton,
  KoobModal,
  KoobSearchBar,
  KoobPlaceholder,
} from '@koob/ui';
import { FolderStatusBadge } from '@koob/booking';
import { getFolderStatus } from '@koob/utils';
import { useAsync } from 'react-async';
import { useDebounce } from 'react-use';
import { useTranslation } from 'react-i18next';
import { getFolder, getFolders } from 'api/folders';
import { useKoobStore } from 'store';
import { openTripWizard } from '@koob/ui/src/features/tripWizard';
import FoldersModal from 'containers/Folders/Partials/FoldersModal';

const guestName = folder =>
  folder?.travelers?.[0]
    ? `${folder.travelers[0].firstName ?? ''} ${folder.travelers[0].lastName ?? ''}`.trim()
    : '';

const agentName = folder =>
  folder?.createdBy
    ? `${folder.createdBy.firstName ?? ''} ${folder.createdBy.lastName ?? ''}`.trim()
    : '';

const providerName = folder => {
  const first = folder?.bookings?.[0];
  if (!first) return '';
  return first.kind === 'dmc'
    ? (first.hotel?.organization?.displayName ??
        first.experiencesBooked?.[0]?.experience?.organizationName ??
        '')
    : 'BedBank';
};

// ponytail: search is server-side (getFolders ?search=), no client filtering or
// pagination — the endpoint already caps the result set.
export default function BrowseFoldersModal({ open, setOpen }) {
  const { t } = useTranslation('folders');
  const openFolder = useKoobStore(s => s.openFolder);
  const intent = useKoobStore(s => s.browseFoldersIntent);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [creating, setCreating] = useState(false);

  useDebounce(() => setDebouncedSearch(search), 500, [search]);

  const { data, isPending, reload } = useAsync({
    promiseFn: getFolders,
    watch: debouncedSearch,
    search: debouncedSearch,
  });

  const folders = data?.data ?? [];

  const afterPick = () => {
    if (intent === 'designTrip') openTripWizard();
  };

  const select = async folder => {
    try {
      const { data: fresh } = await getFolder({ id: folder.id });
      if (fresh.archivedAt) throw new Error('archived');
      openFolder(fresh, 'selected');
      setOpen(false);
      afterPick();
    } catch (error) {
      notify(t('activeFolder.selectFailed', { name: folder.name }), {
        type: 'error',
      });
      reload();
    }
  };

  const create = () => {
    setOpen(false);
    setCreating(true);
  };

  const closeCreate = value => {
    setCreating(value);
    if (!value) setOpen(true, intent);
  };

  return (
    <>
      <KoobModal
        open={open}
        setOpen={setOpen}
        widthClass="sm:max-w-2xl"
        showCloseIcon
        autoFocus={false}
      >
        <div className="flex flex-col gap-3">
          <div>
            <div className="text-lg font-semibold text-k-h">
              {t('activeFolder.browse')}
            </div>
            <div className="text-sm text-k-p">
              {t('activeFolder.browseHelp')}
            </div>
          </div>

          <KoobSearchBar
            value={search}
            onChange={setSearch}
            placeholder={t('activeFolder.browsePlaceholder')}
          />

          <div className="max-h-[55vh] overflow-y-auto -mx-4 sm:-mx-6 border-t border-gray-200">
            {isPending ? (
              <div className="p-4">
                <KoobPlaceholder className="h-40 w-full" />
              </div>
            ) : folders.length === 0 ? (
              <div className="p-6 text-sm text-k-p text-center">
                {debouncedSearch
                  ? t('activeFolder.browseNoMatch', { query: debouncedSearch })
                  : t('activeFolder.browseNoFolders')}
              </div>
            ) : (
              <ul className="divide-y divide-gray-200 m-0 p-0 list-none">
                {folders.map(folder => (
                  <li key={folder.id}>
                    <button
                      type="button"
                      onClick={() => select(folder)}
                      className="w-full text-left border-none bg-transparent hover:bg-gray-50 cursor-pointer px-4 sm:px-6 py-3 flex items-center gap-4"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold text-brand">
                            {folder.icr}
                          </span>
                          <span className="text-sm font-medium text-k-h truncate">
                            {folder.name}
                          </span>
                        </div>
                        <div className="text-xs text-k-p truncate mt-0.5">
                          {[
                            guestName(folder),
                            agentName(folder),
                            providerName(folder),
                          ]
                            .map(v => v || '—')
                            .join(' · ')}
                        </div>
                      </div>
                      <FolderStatusBadge status={getFolderStatus(folder)} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="-mx-4 sm:-mx-6 -mb-4 sm:-mb-6 px-4 sm:px-6 py-4 border-t border-gray-200 bg-orange-50">
            <KoobButton
              variant="primary"
              block
              onClick={create}
              style={{ height: 'auto' }}
            >
              <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-white/20 text-lg font-semibold leading-none">
                +
              </span>
              <span className="flex flex-col flex-1 text-left py-2">
                <span className="text-sm font-bold">
                  {t('activeFolder.browseCreate')}
                </span>
                <span className="text-xs font-normal opacity-85">
                  {t('activeFolder.browseCreateHelp')}
                </span>
              </span>
              <span aria-hidden="true">→</span>
            </KoobButton>
          </div>
        </div>
      </KoobModal>
      <FoldersModal
        open={creating}
        setOpen={closeCreate}
        skipSuccessScreen
        onFolderCreated={folder => {
          openFolder(folder, 'selected');
          setCreating(false);
          afterPick();
        }}
      />
    </>
  );
}
